import classNames from "classnames";
import { PropTypes } from "prop-types";
import qs from "qs";
import React from "react";
import autoBind from "react-autobind";
import { toast } from "react-toastify";
import AttentionNeeded from "~/components/attention_needed";
import DrawerManager from "~/components/drawers/drawer_manager";
import NavBar from "~/components/navigation/nav_bar";
import GlobalNotices from "~/components/shared/global_notices";
import DrawerContext from "~/contexts/DrawerContext";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import dayjs from "~/lib/dayjs";

// Returns true when the path's product is access-restricted by a posthog
// feature flag, so a deep-link drawer (?lead=...) should NOT auto-open
// while NavBar is about to redirect us out of the restricted product.
// Opening the drawer anyway races NavBar's redirect against LeadDrawer's
// own pipeline redirect (lead_drawer.jsx onLeadDrawerStoreChange) and
// can produce a "Loading Lead..." spinner that never resolves.
const isPathProductRestricted = (pathname) => {
  if (pathname.startsWith("/retention")) {
    return checkFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_RESTRICT_RETENTION_ACCESS_FLAG,
    );
  }
  return false;
};

/**
 * Global Container component
 * Responsible for executing logic at global level
 */

class GlobalContainer extends React.Component {
  /**
   * Return location.search query as object: ?a=b&x=y => { a: b, x: y }
   * @returns {Object} params
   */
  static urlParams() {
    if (!window.location.search) {
      return {};
    }

    const locationSearch = window.location.search.replace("?", "");
    return qs.parse(locationSearch);
  }

  static notify(message, type) {
    const opts = {
      position: "top-right",
      closeOnClick: true,
      autoClose: 3000,
    };

    switch (type) {
      case "success":
        toast.success(
          <div>{message}</div>,
          Object.assign(opts, { className: "success-toast" }),
        );
        break;
      case "info":
        toast.info(
          <div>{message}</div>,
          Object.assign(opts, { className: "info-toast" }),
        );
        break;
      case "warn":
      case "warning":
        toast.warn(
          <div>{message}</div>,
          Object.assign(opts, { className: "warning-toast" }),
        );
        break;
      case "error":
        toast.error(
          <div dangerouslySetInnerHTML={{ __html: message }} />,
          Object.assign(opts, { className: "danger-toast" }),
        );
        break;
      default:
        switch (GlobalContainer.product()) {
          case "recruiting":
            toast.success(
              <div>{message}</div>,
              Object.assign(opts, { className: "success-toast" }),
            );
            break;
          case "retention":
            toast.info(
              <div>{message}</div>,
              Object.assign(opts, { className: "info-toast" }),
            );
            break;
          default:
            toast.success(
              <div>{message}</div>,
              Object.assign(opts, {
                className: "success-toast",
              }),
            );
        }
    }
  }

  static renderErrors(errors) {
    const errorsContent = [];
    _lodash.forEach(errors, (v, k) => {
      errorsContent.push(`${_lodash.titleize(k)} ${(Array.isArray(v) ? v : [v]).join(", ")}`);
    });

    return errorsContent.join(", ");
  }

  /**
   * Returns the product mode (or none if not in product mode)
   * @returns {String} product
   */
  static product() {
    const path = window.location.pathname;
    switch (true) {
      case /^\/recruiter\/archived/.test(path):
        return "archived";
      case /^\/recruiter/.test(path):
        return "recruiting";
      case /^\/retention/.test(path):
        return "retention";
      case /^\/agent/.test(path):
        return "agent";
      case /^\/archived/.test(path):
        return "archived";
      case /^\/campaign_libraries/.test(path):
        return "recruiting";
      default:
        return "default";
    }
  }

  static productUriPrefix() {
    const product = this.product();

    if (product === "recruiting") {
      return "/recruiter";
    }
    if (product === "retention") {
      return "/retention";
    }

    return "";
  }

  static productUri(url) {
    return `${this.productUriPrefix()}${url}`;
  }

  /**
   * Returns the product mode (or none if not in product mode)
   * @returns {String} product
   */
  static productColor() {
    const path = window.location.pathname;
    switch (true) {
      case /^\/recruiter/.test(path):
        return "green";
      case /^\/retention/.test(path):
        return "blue";
      case /^\/agent/.test(path):
        return "green";
      default:
        return "green";
    }
  }

  constructor(props) {
    super(props);
    this.state = {};
    autoBind(this);
  }

  componentDidMount() {
    let searchState = {};

    if (window.location.search) {
      searchState = qs.parse(
        window.location.search.toString().replace("?", ""),
      );
    }
    if (searchState.lead) {
      // Skip auto-opening the drawer when the user is restricted from
      // the current product. NavBar's gate effect is about to redirect
      // them out; opening the drawer here races that redirect against
      // LeadDrawer's pipeline redirect and can stall the UI.
      if (isPathProductRestricted(window.location.pathname)) {
        return;
      }

      this.openDrawer("lead", {
        loadLeadID: searchState.lead,
        activityId: searchState.activity_id,
        defaultTab: searchState.tab,
      });
    }
  }

  /**
   * Open the LeadDrawer by a reloaded Lead or load Lead by ID
   * @param {String} kind - Lead load options, support: lead, loadLeadID, defaultAction
   * @param {Object} options - Lead load options, support: lead, loadLeadID, defaultAction
   */
  openDrawer = (kind, options = {}) => {
    this.drawerManager.open(kind, options);
  };

  /**
   * Close lead drawer
   */
  closeDrawer = () => {
    if (this.drawerManager) {
      this.drawerManager.close();
    }
  };

  render() {
    const { widgets, children, showHeader } = this.props;

    return (
      <DrawerContext.Provider
        value={{ openDrawer: this.openDrawer, closeDrawer: this.closeDrawer }}
      >
        <div
          className={classNames(GlobalContainer.product(), "content-container")}
          style={{ minHeight: "calc(100vh - 20px)" }}
        >
          <GlobalNotices />
          {showHeader && <NavBar />}

          <div className="mt15">
            {widgets && <AttentionNeeded />}
            <div className="main react-app">
              <div className="container-fluid p15">{children}</div>
            </div>
          </div>
        </div>

        <footer className="" style={{ minHeight: "20px" }}>
          <span className="ml5">
            &copy; {dayjs().format("YYYY")} BrokerKit Services Inc.
            <a href="/home/terms-of-service/" className="ml15 mr15">
              Terms
            </a>
            <a href="/home/privacy-policy/">Privacy</a>
          </span>
        </footer>

        <DrawerManager ref={(el) => (this.drawerManager = el)} />
      </DrawerContext.Provider>
    );
  }
}

GlobalContainer.defaultProps = {
  children: null,
  showHeader: true,
};

GlobalContainer.propTypes = {
  children: PropTypes.node,
  showHeader: PropTypes.bool,
};

export { DrawerContext };
export default GlobalContainer;
